import React, { useContext, useRef, useState } from 'react';
import styles from './styles/NavbarNotifications.module.css';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import NotificationContext from '../../../context/NotificationContext';
import { getNotificationDestination } from '../../../utils/notificationNavigation';
import { transformIframes } from '../../../utils/utils';
import Loading from '../../loading/Loading.js';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faBullhorn,
    faComment,
    faCommentDots,
    faCheckCircle,
    faBell
} from '@fortawesome/free-solid-svg-icons';

export const getNotificationIcon = (type) => {
    switch (type) {
        case 'invite':
            return faUserPlus;
        case 'announcement':
            return faBullhorn;
        case 'feedback':
        case 'siteFeedback':
            return faComment;
        case 'feedbackMessage':
            return faCommentDots;
        case 'acceptedInvite':
            return faCheckCircle;
        default:
            return faBell;
    }
};

const NavbarNotifications = ({ setShowNotifications }) => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const {
        notifications,
        markAsSeen,
        acceptInvite: acceptInviteAction,
        ignoreInvite: ignoreInviteAction,
    } = useContext(NotificationContext);
    const notificationsRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const [visibleNotifications, setVisibleNotifications] = useState(5);

    const handleNotificationClick = async (notification) => {
        if (!notification || !notification._id || notification.notificationType === 'invite') {
            return;
        }
        if (!notification.seen) {
            try {
                await markAsSeen(notification._id);
            } catch (err) {
                console.error("Error marking notification as seen:", err);
            }
        }

        const destination = getNotificationDestination(notification, user?.username);
        if (destination) {
            navigate(destination.path, destination.state ? { state: destination.state } : undefined);
        }
        setShowNotifications(false);
    };

    const acceptInvite = async (relatedClassroomId, role, notificationId) => {
        setLoading(true);
        try {
            await acceptInviteAction(relatedClassroomId, role, notificationId);
            setShowNotifications(false);
            navigate(`/my-classrooms/${relatedClassroomId}/d`);
        } catch (err) {
            console.error('An error occurred:', err);
        } finally {
            setLoading(false);
        }
    };

    const ignoreInvite = async (notificationId) => {
        setLoading(true);
        try {
            await ignoreInviteAction(notificationId);
            setShowNotifications(false);
        } catch (err) {
            console.error('An error occurred:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreNotifications = () => {
        setVisibleNotifications((prev) => prev + 6);
    };

    const sortedNotifications = [...notifications].sort((a, b) => {
        if (a.seen === b.seen) {
            return new Date(b.time) - new Date(a.time);
        }
        return a.seen ? 1 : -1;
    });

    if (loading) {
        return <div className={styles.dropdown}><Loading/></div>
    }

    return (
        <div
            ref={notificationsRef}
            className={styles.dropdown}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={styles.header}>
                <h2>Notifications</h2>
            </div>

            {sortedNotifications.length > 0 ? (
                <>
                    {sortedNotifications.slice(0, visibleNotifications).map((notification) => (
                        <div
                            key={notification._id || `${notification.notificationType}-${notification.time}`}
                            className={`${styles.notificationItem} ${!notification.seen ? styles.unseen : ''
                                }`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className={styles.iconContainer}>
                                <FontAwesomeIcon
                                    icon={getNotificationIcon(notification.notificationType)}
                                    className={styles.notificationIcon}
                                />
                            </div>
                            <div className={styles.textContainer}>
                                <p
                                    className={styles.notificationMessage}
                                    dangerouslySetInnerHTML={{
                                        __html: transformIframes(notification.message)
                                    }}
                                />
                                {notification.notificationType === 'invite' && (
                                    <div className={styles.notificationActions}>
                                        <button
                                            className={`${styles.actionButton} ${styles.acceptButton}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                acceptInvite(
                                                    notification.relatedClassroomId,
                                                    notification.role,
                                                    notification._id
                                                );
                                            }}
                                            disabled={loading}
                                        >
                                            Accept
                                        </button>
                                        <button
                                            className={`${styles.actionButton} ${styles.ignoreButton}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                ignoreInvite(notification._id);
                                            }}
                                            disabled={loading}
                                        >
                                            Ignore
                                        </button>
                                    </div>
                                )}
                            </div>
                            {!notification.seen && <span className={styles.blueDot} />}
                        </div>
                    ))}
                    {visibleNotifications < sortedNotifications.length && (
                        <div className={styles.loadMoreContainer}>
                            <button
                                className={styles.loadMoreButton}
                                onClick={loadMoreNotifications}
                            >
                                See More
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.noNotifications}>
                    You have no new notifications
                </div>
            )}
        </div>
    );
};

export default NavbarNotifications;
